// src/pages/Settings.jsx
import React, { useState } from 'react';
import { 
  Settings as SettingsIcon, 
  Monitor, 
  Users, 
  Database, 
  Download, 
  Upload,
  Trash2,
  Save,
  RefreshCw,
  Bell,
  Shield,
  Globe,
  Palette
} from 'lucide-react';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({
    // Paramètres généraux
    siteName: 'La Grange des Petits',
    timezone: 'Europe/Paris',
    language: 'fr-FR',
    dateFormat: 'DD/MM/YYYY',
    autoSave: true,
    
    // Paramètres d'affichage
    defaultResolution: '1920x1080',
    refreshInterval: 30,
    errorDisplayDuration: 5,
    
    // Notifications
    emailNotifications: true,
    displayOfflineAlert: true,
    contentUpdateAlert: false,
    
    // Sécurité
    sessionTimeout: 60,
    requireAuth: true,
    passwordExpiry: 90
  });

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleExport = () => {
    const dataStr = JSON.stringify({
      settings,
      templates: JSON.parse(localStorage.getItem('templates') || '[]'),
      displays: JSON.parse(localStorage.getItem('displays') || '[]'),
      schedules: JSON.parse(localStorage.getItem('schedules') || '[]'),
      exportDate: new Date().toISOString()
    }, null, 2);
    
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `digital-signage-backup-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        
        if (data.settings) {
          setSettings(data.settings);
        }
        
        if (data.templates) {
          localStorage.setItem('templates', JSON.stringify(data.templates));
        }
        
        if (data.displays) {
          localStorage.setItem('displays', JSON.stringify(data.displays));
        }
        
        if (data.schedules) {
          localStorage.setItem('schedules', JSON.stringify(data.schedules));
        }
        
        alert('Configuration importée avec succès !');
      } catch (error) {
        alert('Erreur lors de l\'importation du fichier');
      }
    };
    reader.readAsText(file);
  };

  const handleReset = () => {
    if (window.confirm('Êtes-vous sûr de vouloir réinitialiser tous les paramètres ?')) {
      localStorage.clear();
      window.location.reload();
    }
  };

  const tabs = [
    { id: 'general', label: 'Général', icon: SettingsIcon },
    { id: 'displays', label: 'Affichages', icon: Monitor },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Sécurité', icon: Shield },
    { id: 'data', label: 'Données', icon: Database }
  ];

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {/* En-tête */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Paramètres</h1>
          <p className="text-gray-600">Configurez votre application d'affichage digital</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Navigation des onglets */}
          <div className="lg:col-span-1">
            <nav className="space-y-1">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 text-left rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-100 text-blue-700 border border-blue-200'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Contenu des onglets */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow border">
              
              {/* Onglet Général */}
              {activeTab === 'general' && (
                <div className="p-6">
                  <h2 className="text-lg font-semibold mb-6">Paramètres Généraux</h2>
                  
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nom du site
                        </label>
                        <input
                          type="text"
                          value={settings.siteName}
                          onChange={(e) => updateSetting('siteName', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Fuseau horaire
                        </label>
                        <select
                          value={settings.timezone}
                          onChange={(e) => updateSetting('timezone', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="Europe/Paris">Europe/Paris</option>
                          <option value="Europe/London">Europe/London</option>
                          <option value="America/New_York">America/New_York</option>
                          <option value="Asia/Tokyo">Asia/Tokyo</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Langue
                        </label>
                        <select
                          value={settings.language}
                          onChange={(e) => updateSetting('language', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="fr-FR">Français</option>
                          <option value="en-US">English</option>
                          <option value="es-ES">Español</option>
                          <option value="de-DE">Deutsch</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Format de date
                        </label>
                        <select
                          value={settings.dateFormat}
                          onChange={(e) => updateSetting('dateFormat', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                          <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                          <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium">Sauvegarde automatique</div>
                        <div className="text-sm text-gray-600">
                          Sauvegarde automatiquement vos modifications
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.autoSave}
                          onChange={(e) => updateSetting('autoSave', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Onglet Affichages */}
              {activeTab === 'displays' && (
                <div className="p-6">
                  <h2 className="text-lg font-semibold mb-6">Paramètres d'Affichage</h2>
                  
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Résolution par défaut
                        </label>
                        <select
                          value={settings.defaultResolution}
                          onChange={(e) => updateSetting('defaultResolution', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="1920x1080">1920x1080 (Full HD)</option>
                          <option value="1080x1920">1080x1920 (Portrait)</option>
                          <option value="3840x2160">3840x2160 (4K)</option>
                          <option value="1366x768">1366x768 (HD)</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Intervalle de rafraîchissement (secondes)
                        </label>
                        <input
                          type="number"
                          value={settings.refreshInterval}
                          onChange={(e) => updateSetting('refreshInterval', parseInt(e.target.value))}
                          min="5"
                          max="300"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Durée d'affichage des erreurs (secondes)
                      </label>
                      <input
                        type="number"
                        value={settings.errorDisplayDuration}
                        onChange={(e) => updateSetting('errorDisplayDuration', parseInt(e.target.value))}
                        min="1"
                        max="60"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-sm text-gray-600 mt-1">
                        Temps d'affichage des messages d'erreur sur les écrans
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Onglet Notifications */}
              {activeTab === 'notifications' && (
                <div className="p-6">
                  <h2 className="text-lg font-semibold mb-6">Notifications</h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium">Notifications par email</div>
                        <div className="text-sm text-gray-600">
                          Recevoir les alertes importantes par email
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.emailNotifications}
                          onChange={(e) => updateSetting('emailNotifications', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium">Alerte écran hors ligne</div>
                        <div className="text-sm text-gray-600">
                          Notifier quand un écran se déconnecte
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.displayOfflineAlert}
                          onChange={(e) => updateSetting('displayOfflineAlert', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium">Mise à jour de contenu</div>
                        <div className="text-sm text-gray-600">
                          Notifier lors des mises à jour de templates
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.contentUpdateAlert}
                          onChange={(e) => updateSetting('contentUpdateAlert', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Onglet Sécurité */}
              {activeTab === 'security' && (
                <div className="p-6">
                  <h2 className="text-lg font-semibold mb-6">Sécurité</h2>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Timeout de session (minutes)
                      </label>
                      <input
                        type="number"
                        value={settings.sessionTimeout}
                        onChange={(e) => updateSetting('sessionTimeout', parseInt(e.target.value))}
                        min="5"
                        max="480"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-sm text-gray-600 mt-1">
                        Durée avant déconnexion automatique
                      </p>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium">Authentification requise</div>
                        <div className="text-sm text-gray-600">
                          Exiger une connexion pour accéder à l'interface
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.requireAuth}
                          onChange={(e) => updateSetting('requireAuth', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Expiration du mot de passe (jours)
                      </label>
                      <input
                        type="number"
                        value={settings.passwordExpiry}
                        onChange={(e) => updateSetting('passwordExpiry', parseInt(e.target.value))}
                        min="30"
                        max="365"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-sm text-gray-600 mt-1">
                        Fréquence de renouvellement obligatoire du mot de passe
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Onglet Données */}
              {activeTab === 'data' && (
                <div className="p-6">
                  <h2 className="text-lg font-semibold mb-6">Gestion des Données</h2>
                  
                  <div className="space-y-6">
                    {/* Export/Import */}
                    <div className="border rounded-lg p-4">
                      <h3 className="font-medium mb-4">Sauvegarde et Restauration</h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <button
                            onClick={handleExport}
                            className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <Download className="h-4 w-4" />
                            <span>Exporter la Configuration</span>
                          </button>
                          <p className="text-sm text-gray-600 mt-2">
                            Télécharge un fichier JSON avec tous vos paramètres
                          </p>
                        </div>
                        
                        <div>
                          <label className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors cursor-pointer">
                            <Upload className="h-4 w-4" />
                            <span>Importer une Configuration</span>
                            <input
                              type="file"
                              accept=".json"
                              onChange={handleImport}
                              className="hidden"
                            />
                          </label>
                          <p className="text-sm text-gray-600 mt-2">
                            Restaure vos paramètres depuis un fichier JSON
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Statistiques de stockage */}
                    <div className="border rounded-lg p-4">
                      <h3 className="font-medium mb-4">Utilisation du Stockage</h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="text-center p-4 bg-blue-50 rounded-lg">
                          <div className="text-2xl font-bold text-blue-600">
                            {JSON.parse(localStorage.getItem('templates') || '[]').length}
                          </div>
                          <div className="text-sm text-gray-600">Templates</div>
                        </div>
                        
                        <div className="text-center p-4 bg-green-50 rounded-lg">
                          <div className="text-2xl font-bold text-green-600">
                            {JSON.parse(localStorage.getItem('displays') || '[]').length}
                          </div>
                          <div className="text-sm text-gray-600">Écrans</div>
                        </div>
                        
                        <div className="text-center p-4 bg-purple-50 rounded-lg">
                          <div className="text-2xl font-bold text-purple-600">
                            {JSON.parse(localStorage.getItem('schedules') || '[]').length}
                          </div>
                          <div className="text-sm text-gray-600">Règles</div>
                        </div>
                      </div>
                    </div>

                    {/* Actions dangereuses */}
                    <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                      <h3 className="font-medium text-red-800 mb-4 flex items-center space-x-2">
                        <Trash2 className="h-4 w-4" />
                        <span>Zone Dangereuse</span>
                      </h3>
                      
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-red-800">Réinitialiser toutes les données</div>
                            <div className="text-sm text-red-600">
                              Supprime définitivement tous les templates, écrans et règles
                            </div>
                          </div>
                          <button
                            onClick={handleReset}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                          >
                            Réinitialiser
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Informations techniques */}
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <h3 className="font-medium mb-4">Informations Techniques</h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <div className="text-gray-600">Version de l'application:</div>
                          <div className="font-medium">v1.0.0</div>
                        </div>
                        
                        <div>
                          <div className="text-gray-600">Stockage utilisé:</div>
                          <div className="font-medium">
                            {Math.round(JSON.stringify(localStorage).length / 1024)} KB
                          </div>
                        </div>
                        
                        <div>
                          <div className="text-gray-600">Navigateur:</div>
                          <div className="font-medium">{navigator.userAgent.split(' ')[0]}</div>
                        </div>
                        
                        <div>
                          <div className="text-gray-600">Dernière sauvegarde:</div>
                          <div className="font-medium">
                            {new Date().toLocaleString('fr-FR')}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Boutons d'action */}
              <div className="border-t border-gray-200 p-6 bg-gray-50">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    Les paramètres sont sauvegardés automatiquement
                  </div>
                  
                  <div className="flex space-x-3">
                    <button
                      onClick={() => window.location.reload()}
                      className="flex items-center space-x-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <RefreshCw className="h-4 w-4" />
                      <span>Actualiser</span>
                    </button>
                    
                    <button
                      onClick={() => {
                        localStorage.setItem('settings', JSON.stringify(settings));
                        alert('Paramètres sauvegardés !');
                      }}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Save className="h-4 w-4" />
                      <span>Sauvegarder</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;